import { useEffect, useRef, useState } from "react";
import { ChevronDownIcon } from "@/components/icons";

interface ScrollMoreHintProps {
  /** Element whose overflow drives the hint. Hint only shows when this
   * container's scrollHeight exceeds its clientHeight. */
  scrollRef: React.RefObject<HTMLElement | null>;
  /** Persistence key so each surface remembers its own seen-state. */
  storageKey: string;
  /** Italian by default; override per surface if needed. */
  label?: string;
  /** Auto-hide after this many ms even without scroll. */
  autoHideMs?: number;
}

const STORAGE_PREFIX = "touchpoint:scroll-hint:";

/**
 * First-run nudge that appears above the bottom pager bar when the
 * review surface has more content below the fold (rating stars, comment
 * field). Dismisses on any scroll, on timeout, or after first display —
 * whichever fires first — and never shows again on the same device for
 * the same surface key.
 *
 * Quiet by default: pill-shaped, neutral palette, bouncing chevron.
 * Lives inside the scroll container as an absolutely-positioned overlay
 * pinned just above the pager bar.
 */
export function ScrollMoreHint({
  scrollRef,
  storageKey,
  label = "Scorri per votare",
  autoHideMs = 4500,
}: ScrollMoreHintProps) {
  const [visible, setVisible] = useState(false);
  const dismissedRef = useRef(false);

  useEffect(() => {
    const fullKey = `${STORAGE_PREFIX}${storageKey}`;
    if (typeof window === "undefined") return;
    if (window.localStorage.getItem(fullKey) === "seen") return;

    const el = scrollRef.current;
    if (!el) return;

    const checkOverflow = () => {
      if (dismissedRef.current) return;
      const overflows = el.scrollHeight - el.clientHeight > 8;
      const atTop = el.scrollTop < 8;
      setVisible(overflows && atTop);
    };

    // Initial probe after layout settles (images, fonts).
    const initialTimer = window.setTimeout(checkOverflow, 250);

    // Re-check when content height changes (image loads, font swap).
    const resizeObserver =
      typeof ResizeObserver !== "undefined"
        ? new ResizeObserver(checkOverflow)
        : null;
    if (resizeObserver) resizeObserver.observe(el);
    for (const child of Array.from(el.children)) {
      resizeObserver?.observe(child);
    }

    const dismiss = () => {
      if (dismissedRef.current) return;
      dismissedRef.current = true;
      setVisible(false);
      window.localStorage.setItem(fullKey, "seen");
      el.removeEventListener("scroll", dismiss);
    };

    el.addEventListener("scroll", dismiss, { passive: true });
    const autoHide = window.setTimeout(dismiss, autoHideMs);

    return () => {
      window.clearTimeout(initialTimer);
      window.clearTimeout(autoHide);
      resizeObserver?.disconnect();
      el.removeEventListener("scroll", dismiss);
    };
  }, [scrollRef, storageKey, autoHideMs]);

  if (!visible) return null;

  return (
    <div
      aria-hidden="true"
      className="pointer-events-none sticky bottom-3 z-20 mx-auto flex w-fit -translate-y-2 items-center gap-1.5 rounded-full bg-gray-900/85 px-3 py-1.5 text-xs font-medium text-white shadow-lg shadow-gray-900/15 backdrop-blur-md animate-in fade-in slide-in-from-bottom-1 duration-300"
    >
      <span>{label}</span>
      <ChevronDownIcon size={14} className="animate-bounce" />
    </div>
  );
}
